/**
 * Hub card — gradient summary, step rows, status row, optional CSS enter swipe.
 *
 * Belongs here: what the user sees in the purple card + `runAfterCardSwipe`.
 * Does not belong here: page chrome or Framer submit centering (see HubInteractiveScreen).
 */
import type { ReactNode } from 'react'
import RenderHTML from '../../../../components/ui-components/RenderHTML'
import { formatRupeeLabel } from '../../plans-v4/display-values/lumpsumPlanAmount'
import {
  DirectionalSlideSwap,
  SlideDirection
} from '../shared/transitions/DirectionalSlideSwap'
import { getContentSlideDurationMs } from '../shared/transitions/contentTransitionTimings'
import { CardBg } from './cardBg'
import {
  HubPresence,
  HubSubmitStatusRow,
  HubSubmitPhase
} from './HubSubmit'
import {
  MonthlySipStepCard,
  PreferencesStepCard,
  StrategyStepCard
} from './hubSteps'

export type HubPortfolioSummary = {
  userName: string
  portfolioValue: number
  fundCount: number
}

/**
 * Run `fn` after the card CSS swipe ends.
 * If there is no swipe, run immediately.
 * Callers: scroll-into-view after enter.
 */
export function runAfterCardSwipe(shouldSwipeCardIn: boolean, fn: () => void): () => void {
  if (!shouldSwipeCardIn) {
    fn()
    return () => { }
  }
  const timeoutId = window.setTimeout(fn, getContentSlideDurationMs())
  return () => window.clearTimeout(timeoutId)
}

/** Interactive-visit card: steps collapse on submit; status row follows phase. */
export function HubCard({
  shouldSwipeCardIn,
  userName,
  portfolioValue,
  fundCount,
  submitPhase,
  shouldPlayCardSubmitPulse,
  firstPlanDropTime
}: {
  shouldSwipeCardIn: boolean
  userName: string
  portfolioValue: number
  fundCount: number
  submitPhase: HubSubmitPhase
  shouldPlayCardSubmitPulse: boolean
  firstPlanDropTime?: number | null
}) {
  const body = (
    <>
      <HubCardBody
        userName={userName}
        portfolioValue={portfolioValue}
        fundCount={fundCount}
        submitPhase={submitPhase}
        shouldPlayCardSubmitPulse={shouldPlayCardSubmitPulse}
      >
        <div className="divide-y divide-[#F0F4F7] overflow-hidden">
          <StrategyStepCard />
          <PreferencesStepCard />
          <MonthlySipStepCard />
        </div>
      </HubCardBody>
      <HubSubmitStatusRow phase={submitPhase} firstPlanDropTime={firstPlanDropTime} />
    </>
  )

  if (!shouldSwipeCardIn) return body

  return (
    <DirectionalSlideSwap contentKey="expert-review-hub-card" direction={SlideDirection.Forward}>
      {body}
    </DirectionalSlideSwap>
  )
}

/**
 * Gradient shell + optional step list.
 * Shared by interactive and submitted visits — steps only while phase is pending.
 * Summary inset is always pt-10 (40px) so submit collapse is steps-only.
 */
export function HubCardBody({
  userName,
  portfolioValue,
  fundCount,
  children,
  submitPhase,
  shouldPlayCardSubmitPulse = false
}: {
  userName: string
  portfolioValue: number
  fundCount: number
  children?: ReactNode
  submitPhase: HubSubmitPhase
  shouldPlayCardSubmitPulse?: boolean
}) {
  // Soft on both ends — top-left and bottom-right fall off together
  const cardPatternMask =
    'linear-gradient(to bottom right, transparent 0%, rgba(0,0,0,0.2) 24%, black 50%, rgba(0,0,0,0.2) 76%, transparent 100%)'

  return (
    <div
      className={`relative w-full overflow-hidden rounded-[16px]${shouldPlayCardSubmitPulse ? ' hub-card--pulse' : ''}`}
      style={{
        // Figma's 220% ellipse puts #EDCEC4 outside the card — farthest-corner lands it on the bottom-right
        background: 'radial-gradient(farthest-corner at 0% 0%, #FCF1ED 0%, #EDCEC4 100%)'
      }}
    >
      <div
        className={`pointer-events-none absolute right-0 top-0 aspect-[156/201] w-[168px] [&>div]:h-full [&>div]:w-full [&>div>svg]:h-full [&>div>svg]:w-full${shouldPlayCardSubmitPulse ? ' hub-card-bg--sending' : ''}`}
        style={{
          maskImage: cardPatternMask,
          WebkitMaskImage: cardPatternMask,
          opacity: 0.95
        }}
        aria-hidden
      >
        <RenderHTML innerHTMLText={CardBg} className="h-full w-full" />
      </div>
      <div className="relative px-6 pt-6 pb-5 min-h-[188px]">
        {userName ? (
          <p className="text-[12px] font-[500] uppercase tracking-[2px] text-[#B85230]">
            {userName}
          </p>
        ) : null}
        <p className="pt-10 text-[13px] font-[400] leading-[150%] text-[#B85230]">
          Portfolio to be reviewed
        </p>
        <p className="pt-1 text-[22px] font-[500] uppercase leading-[150%] text-[#253253]">
          {formatRupeeLabel({ rawAmount: portfolioValue })}
        </p>
        <p className="pt-[2px] text-[22px] font-[500] leading-[150%] text-[#253253]">
          {fundCount === 1 ? '1 fund' : `${fundCount} funds`}
        </p>
      </div>
      <HubPresence show={submitPhase === HubSubmitPhase.Pending}>
        <div className="relative mx-[2px] mb-[2px] overflow-hidden rounded-[14px] bg-white">
          {children}
        </div>
      </HubPresence>
      {shouldPlayCardSubmitPulse ? (
        <>
          <span aria-hidden className="hub-card-breathe" />
          <span aria-hidden className="hub-card-shimmer" />
        </>
      ) : null}
    </div>
  )
}
